import type { ReferenceDataField } from '@moment/trading-types/instrument-data'

import {
  type FilterableField,
  FilterValueType,
} from '@moment/trading-types/filter'
import {
  AccretionCompoundingMethod,
  BondFormType,
  BondStatus,
  BondType,
  CallFrequency,
  CallNotificationConvention,
  CallType,
  CocoEventType,
  CocoTriggerAction,
  CocoTriggerLevelType,
  CocoTriggerType,
  CountryDomicile,
  CountryIssue,
  CouponFrequency,
  CouponSubtype,
  CouponType,
  CouponTypeMethod,
  CoveredBondCollateral,
  CreditQualityClass,
  Currency,
  DayCount,
  EmergingStatus,
  FitchOutlook,
  FitchRating,
  FloatResetFrequency,
  GicsIndustry,
  GicsIndustryGroup,
  GicsSector,
  GicsSubIndustry,
  IceSector1,
  IceSector2,
  IceSector3,
  IceSector4,
  ImpactBondType,
  InstrumentClass,
  MoodysOutlook,
  MoodysRating,
  MunicipalSector,
  MunicipalTaxStatus,
  MunicipalType,
  NaicsSector,
  NaicsSubsector,
  Purpose,
  Sector,
  SecurityCode,
  Seniority,
  SeniorityType,
  SettlementConvention,
  SinkFundRedemptionMethod,
  SPRating,
  SpCreditWatch,
  SpOutlook,
  StripType,
  SukukContractType,
  SukukType,
  TradingRestrictionsType,
  TreasurySubtype,
  UseOfProceeds,
} from '@moment/trading-types/reference-data'
import { type IStateResult, UsaStates } from 'usa-states'

const STATE_OPTIONS = new UsaStates({
  includeTerritories: true,
  contiguousOnly: false,
  exclude: [],
  ignoreCharacter: '$',
}).states.map((state: IStateResult) => ({
  label: state.name,
  value: state.abbreviation,
}))

// Field reference options: maps source fields to the target fields they can be compared against.
// When a source field is selected, these target fields appear as options in a "Compare to field" dropdown.
export const FIELD_REFERENCE_OPTIONS: Partial<
  Record<
    ReferenceDataField | 'days_to_maturity',
    Array<{ label: string; value: string }>
  >
> = {
  days_to_next_sink: [
    { label: 'Call Notification Min', value: 'call_notification_min' },
  ],
}

export const DEFAULT_FILTER_BUILDER_CONDITION = {
  field: null,
  operator: null,
  inputValue: '',
}

export const DEFAULT_FILTER_BUILDER_STATE = {
  field: null,
  operator: null,
  value: [],
}

// TODO: Tech debt - move the enum logic to global types folder and update instrumentDataTransforms to include the type and dropdowOptions for each referenceDataTransformationConfig.
export const FILTER_FIELDS: FilterableField[] = [
  {
    id: 'is_144a',
    label: '144A',
    type: FilterValueType.Boolean,
  },
  {
    id: 'accretion_compounding_method',
    label: 'Accretion Compounding Method',
    type: FilterValueType.String,
    dropdownOptions: Object.values(AccretionCompoundingMethod),
  },
  {
    id: 'accrued_interest',
    label: 'Accrued Interest',
    type: FilterValueType.Number,
  },
  {
    id: 'accrued_interest_date',
    label: 'Accrued Interest Date',
    type: FilterValueType.Date,
  },
  // {
  //   id: 'ai_issuer',
  //   label: 'AI Issuer',
  //   type: FilterValueType.String,
  // },
  {
    id: 'amount_outstanding',
    label: 'Amount Outstanding',
    type: FilterValueType.Number,
  },
  {
    id: 'amount_outstanding_date',
    label: 'Amount Outstanding Date',
    type: FilterValueType.Date,
  },
  {
    id: 'ask_g_spread',
    label: 'G-spread (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_i_spread',
    label: 'I-spread (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_oas',
    label: 'OAS (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_price',
    label: 'Ask Price',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_ytc',
    label: 'Yield to call (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_ytm',
    label: 'Yield to maturity (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_ytw',
    label: 'Yield to worst (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_ytw_spread_bps',
    label: 'Spread to benchmark (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'ask_z_spread',
    label: 'Z-spread (ask)',
    type: FilterValueType.Number,
  },
  {
    id: 'bank_qualified',
    label: 'Bank Qualified',
    type: FilterValueType.Boolean,
  },
  {
    id: 'benchmark_isin',
    label: 'Benchmark ISIN',
    type: FilterValueType.StringFlex,
  },
  {
    id: 'benchmark_yield',
    label: 'Benchmark Yield',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_g_spread',
    label: 'G-spread (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_i_spread',
    label: 'I-spread (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_oas',
    label: 'OAS (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_price',
    label: 'Bid Price',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_ytc',
    label: 'Yield to call (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_ytm',
    label: 'Yield to maturity (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_ytw',
    label: 'Yield to worst (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_ytw_spread_bps',
    label: 'Spread to benchmark (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bid_z_spread',
    label: 'Z-spread (bid)',
    type: FilterValueType.Number,
  },
  {
    id: 'bond_form_type',
    label: 'Bond Form Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(BondFormType),
  },
  {
    id: 'bond_warrant',
    label: 'Bond Warrant',
    type: FilterValueType.Boolean,
  },
  {
    id: 'bullet',
    label: 'Bullet',
    type: FilterValueType.Boolean,
  },
  {
    id: 'call_announced',
    label: 'Call Announced',
    type: FilterValueType.Boolean,
  },
  {
    id: 'call_frequency',
    label: 'Call Frequency',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CallFrequency),
  },
  {
    id: 'call_notification_convention',
    label: 'Call Notification Convention',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CallNotificationConvention),
  },
  {
    id: 'call_notification_max',
    label: 'Call Notification Max',
    type: FilterValueType.Number,
  },
  {
    id: 'call_notification_min',
    label: 'Call Notification Min',
    type: FilterValueType.Number,
  },
  // 'call_schedule' ommitted - not useful in the filter builder
  {
    id: 'call_type',
    label: 'Call Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CallType),
  },
  {
    id: 'callable',
    label: 'Callable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'coco_event_type',
    label: 'Trigger Event Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CocoEventType),
  },
  {
    id: 'coco_indicator',
    label: 'CoCo Indicator',
    type: FilterValueType.Boolean,
  },
  {
    id: 'coco_trigger_action',
    label: 'Trigger Action',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CocoTriggerAction),
  },
  {
    id: 'coco_trigger_level_type',
    label: 'Trigger Level Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CocoTriggerLevelType),
  },
  {
    id: 'coco_trigger_level_value',
    label: 'Trigger Level Value',
    type: FilterValueType.String,
  },
  {
    id: 'coco_trigger_type',
    label: 'Trigger Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CocoTriggerType),
  },
  {
    id: 'close_price',
    label: 'Close Price',
    type: FilterValueType.Number,
  },
  {
    id: 'close_price_asof',
    label: 'Close Price Asof',
    type: FilterValueType.Date,
  },
  {
    id: 'close_ytm',
    label: 'Close YTM',
    type: FilterValueType.Number,
  },
  {
    id: 'close_ytw',
    label: 'Close YTW',
    type: FilterValueType.Number,
  },
  {
    id: 'continuously_callable_flag',
    label: 'Continuously Callable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'currently_callable',
    label: 'Currently Callable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'convertible',
    label: 'Convertible',
    type: FilterValueType.Boolean,
  },
  {
    id: 'convexity',
    label: 'Convexity',
    type: FilterValueType.Number,
  },
  {
    id: 'country_domicile',
    label: 'Country of Domicile',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CountryDomicile), // ISO codes (e.g., 'US', 'AU')
  },
  {
    id: 'country_issue',
    label: 'Country of Issue',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CountryIssue), // ISO codes (e.g., 'US', 'CA')
  },
  {
    id: 'country_of_risk',
    label: 'Country of Risk',
    type: FilterValueType.String,
    // Full ISO list, deliberately a superset of the codes currently present in
    // reference data — a country with no outstanding bonds today may have one
    // tomorrow.
    dropdownOptions: Object.values(CountryDomicile),
  },
  {
    id: 'coupon',
    label: 'Coupon',
    type: FilterValueType.Number,
  },
  {
    id: 'coupon_frequency',
    label: 'Coupon Frequency',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CouponFrequency),
  },
  {
    id: 'coupon_inflation_protected',
    label: 'Inflation Protected Coupon',
    type: FilterValueType.Boolean,
  },
  {
    id: 'coupon_subtype',
    label: 'Coupon Subtype',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CouponSubtype),
  },
  {
    id: 'coupon_summary',
    label: 'Coupon Summary',
    type: FilterValueType.String,
  },
  {
    id: 'coupon_type',
    label: 'Coupon Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CouponType),
  },
  {
    id: 'coupon_type_method',
    label: 'Coupon Type Method',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CouponTypeMethod),
  },
  {
    id: 'covered_bond_collateral',
    label: 'Covered Bond Collateral',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CoveredBondCollateral),
  },
  {
    id: 'credit_sensitive_indicator',
    label: 'Credit Sensitive',
    type: FilterValueType.Boolean,
  },
  {
    id: 'currency',
    label: 'Currency',
    type: FilterValueType.String,
    dropdownOptions: Object.values(Currency),
  },
  {
    id: 'cusip',
    label: 'CUSIP',
    type: FilterValueType.StringFlex,
  },
  {
    id: 'dated_date',
    label: 'Dated Date',
    type: FilterValueType.Date,
  },
  {
    id: 'daycount',
    label: 'Day count',
    type: FilterValueType.String,
    dropdownOptions: Object.values(DayCount),
  },
  {
    id: 'days_to_maturity',
    label: 'Days to Maturity',
    type: FilterValueType.Number,
  },
  {
    id: 'days_to_next_sink',
    label: 'Days to Next Sink',
    type: FilterValueType.Number,
  },
  // 'description omitted - not useful in the filter builder
  // 'description_short omitted - not useful in the filter builder
  {
    id: 'default_indicator',
    label: 'In Default',
    type: FilterValueType.Boolean,
  },
  {
    id: 'discretionary_coupon',
    label: 'Discretionary Coupon',
    type: FilterValueType.Boolean,
  },
  {
    id: 'duration',
    label: 'Modified Duration',
    type: FilterValueType.Number,
  },
  {
    id: 'duration_date',
    label: 'Duration Date',
    type: FilterValueType.Date,
  },
  {
    id: 'duration_times_spread',
    label: 'Duration x Spread',
    type: FilterValueType.Number,
  },
  {
    id: 'effective_call_date',
    label: 'Effective Call Date',
    type: FilterValueType.Date,
  },
  {
    id: 'effective_call_price',
    label: 'Effective Call Price',
    type: FilterValueType.Number,
  },
  {
    id: 'effective_duration',
    label: 'Effective Duration',
    type: FilterValueType.Number,
  },
  {
    id: 'emerging_status',
    label: 'Emerging Market Status',
    type: FilterValueType.String,
    dropdownOptions: Object.values(EmergingStatus),
  },
  {
    id: 'credit_quality_class',
    label: 'Credit Quality',
    type: FilterValueType.String,
    dropdownOptions: Object.values(CreditQualityClass),
  },
  {
    id: 'eval_price',
    label: 'Price',
    type: FilterValueType.Number,
  },
  {
    id: 'eval_ytm',
    label: 'Yield to maturity (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'eval_ytw',
    label: 'Yield to worst (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'fdic_code',
    label: 'FDIC Code',
    type: FilterValueType.String,
  },
  {
    id: 'fdic_insured',
    label: 'FDIC Insured',
    type: FilterValueType.Boolean,
  },
  {
    id: 'fitch_outlook',
    label: 'Fitch Outlook',
    type: FilterValueType.String,
    dropdownOptions: Object.values(FitchOutlook),
  },
  {
    id: 'fitch_outlook_date',
    label: 'Fitch Outlook Date',
    type: FilterValueType.Date,
  },
  {
    id: 'fitch_rating',
    label: 'Fitch Rating',
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(FitchRating),
  },
  {
    id: 'fitch_rating_date',
    label: 'Fitch Rating Date',
    type: FilterValueType.Date,
  },
  {
    id: 'fitch_underlying_rating',
    label: 'Fitch Underlying Rating',
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(FitchRating),
  },
  {
    id: 'fitch_underlying_rating_date',
    label: 'Fitch Underlying Rating Date',
    type: FilterValueType.Date,
  },
  {
    id: 'first_coupon_date',
    label: 'First Coupon Date',
    type: FilterValueType.Date,
  },
  {
    id: 'float_benchmark',
    label: 'Floating Rate Benchmark',
    type: FilterValueType.String,
  },
  {
    id: 'float_formula_reset_date',
    label: 'Float Formula Reset Date',
    type: FilterValueType.Date,
  },
  {
    id: 'float_max_rate',
    label: 'Float Max Rate',
    type: FilterValueType.Number,
  },
  {
    id: 'float_min_rate',
    label: 'Float Min Rate',
    type: FilterValueType.Number,
  },
  {
    id: 'float_reset_frequency',
    label: 'Float Reset Frequency',
    type: FilterValueType.String,
    dropdownOptions: Object.values(FloatResetFrequency),
  },
  {
    id: 'float_spread',
    label: 'Float Spread',
    type: FilterValueType.Number,
  },
  {
    id: 'has_extraordinary_call',
    label: 'Has Extraordinary Call',
    type: FilterValueType.Boolean,
  },
  {
    id: 'insured',
    label: 'Insured',
    type: FilterValueType.Boolean,
  },
  {
    id: 'insurer',
    label: 'Insurer',
    type: FilterValueType.String,
  },
  {
    id: 'is_amt_applicable',
    label: 'Is AMT Applicable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'is_pre_refunded',
    label: 'Is Pre-Refunded',
    type: FilterValueType.Boolean,
  },
  {
    id: 'is_state_tax_applicable',
    label: 'Is State Tax Applicable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'is_structured_product',
    label: 'Is Structured Product',
    type: FilterValueType.Boolean,
  },
  {
    id: 'isin',
    label: 'ISIN',
    type: FilterValueType.StringFlex,
  },
  {
    id: 'ice_sector_1',
    label: 'ICE Sector 1',
    type: FilterValueType.String,
    dropdownOptions: Object.values(IceSector1),
  },
  {
    id: 'ice_sector_2',
    label: 'ICE Sector 2',
    type: FilterValueType.String,
    dropdownOptions: Object.values(IceSector2),
  },
  {
    id: 'ice_sector_3',
    label: 'ICE Sector 3',
    type: FilterValueType.String,
    dropdownOptions: Object.values(IceSector3),
  },
  {
    id: 'ice_sector_4',
    label: 'ICE Sector 4',
    type: FilterValueType.String,
    dropdownOptions: Object.values(IceSector4),
  },
  {
    id: 'impact_bond_type',
    label: 'Impact Bond Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(ImpactBondType),
  },
  {
    id: 'increment_amount_secondary',
    label: 'Secondary Increment Amount',
    type: FilterValueType.Number,
  },
  {
    id: 'issue_date',
    label: 'Issue Date',
    type: FilterValueType.Date,
  },
  {
    id: 'issue_increment',
    label: 'Issue Increment',
    type: FilterValueType.Number,
  },
  {
    id: 'issue_minimum',
    label: 'Issue Minimum',
    type: FilterValueType.Number,
  },
  {
    id: 'issue_minimum_denomination',
    label: 'Issue Minimum Denomination',
    type: FilterValueType.Number,
  },
  {
    id: 'issue_price',
    label: 'Issue Price',
    type: FilterValueType.Number,
  },
  {
    id: 'issue_size',
    label: 'Issue Size',
    type: FilterValueType.Number,
  },
  {
    id: 'issuer',
    label: 'Issuer',
    type: FilterValueType.String,
  },
  {
    id: 'last_coupon_date',
    label: 'Last Coupon Date',
    type: FilterValueType.Date,
  },
  {
    id: 'liquidity_institutional_aggregate',
    label: 'Liquidity Institutional Aggregate',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_institutional_buy',
    label: 'Liquidity Institutional Buy',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_institutional_sell',
    label: 'Liquidity Institutional Sell',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_micro_aggregate',
    label: 'Liquidity Micro Aggregate',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_micro_buy',
    label: 'Liquidity Micro Buy',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_micro_sell',
    label: 'Liquidity Micro Sell',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_retail_aggregate',
    label: 'Liquidity Retail Aggregate',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_retail_buy',
    label: 'Liquidity Retail Buy',
    type: FilterValueType.Number,
  },
  {
    id: 'liquidity_retail_sell',
    label: 'Liquidity Retail Sell',
    type: FilterValueType.Number,
  },
  {
    id: 'lowest_rating',
    label: 'Lowest Rating',
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(SPRating),
  },
  {
    id: 'macaulay_duration',
    label: 'Macaulay Duration',
    type: FilterValueType.Number,
  },
  {
    id: 'make_whole_benchmark',
    label: 'Make Whole Benchmark',
    type: FilterValueType.String,
  },
  {
    id: 'make_whole_callable',
    label: 'Make Whole Callable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'make_whole_end_date',
    label: 'Make Whole End Date',
    type: FilterValueType.Date,
  },
  {
    id: 'make_whole_spread',
    label: 'Make Whole Spread',
    type: FilterValueType.Number,
  },
  {
    id: 'maturity_date',
    label: 'Maturity Date',
    type: FilterValueType.Date,
  },
  {
    id: 'maturity_in_years',
    label: 'Years to Maturity',
    type: FilterValueType.Number,
  },
  {
    id: 'maturity_redemption_price',
    label: 'Maturity Redemption Price',
    type: FilterValueType.Number,
  },
  {
    id: 'maturity_type',
    label: 'Maturity Type',
    type: FilterValueType.String,
  },
  {
    id: 'maturity_year',
    label: 'Maturity Year',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_g_spread',
    label: 'G-spread (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_i_spread',
    label: 'I-spread (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_oas',
    label: 'OAS (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_price',
    label: 'Mid Price',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_ytc',
    label: 'Yield to call (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_ytw_spread_bps',
    label: 'Spread to benchmark (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'mid_z_spread',
    label: 'Z-spread (mid)',
    type: FilterValueType.Number,
  },
  {
    id: 'muni_sector',
    label: 'Municipal Sector',
    type: FilterValueType.String,
    dropdownOptions: Object.values(MunicipalSector),
  },
  {
    id: 'muni_obligor_state',
    label: 'Municipal Obligor State',
    type: FilterValueType.String,
    dropdownOptions: STATE_OPTIONS.map((state) => state.value),
  },
  {
    id: 'muni_tax_status',
    label: 'Municipal Federal Tax Status',
    type: FilterValueType.String,
    dropdownOptions: Object.values(MunicipalTaxStatus),
  },
  {
    id: 'municipal_state',
    label: 'Municipal State',
    type: FilterValueType.String,
    dropdownOptions: STATE_OPTIONS.map((state) => state.value),
  },
  {
    id: 'municipal_taxable_federal',
    label: 'Municipal Taxable Federal',
    type: FilterValueType.Boolean,
  },
  {
    id: 'municipal_type',
    label: 'Municipal Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(MunicipalType),
  },
  {
    id: 'naics_sector',
    label: 'NAICS Sector',
    type: FilterValueType.String,
    dropdownOptions: Object.values(NaicsSector),
  },
  {
    id: 'naics_subsector',
    label: 'Subsector',
    type: FilterValueType.String,
    dropdownOptions: Object.values(NaicsSubsector),
  },
  {
    id: 'next_call_date',
    label: 'Next Call Date',
    type: FilterValueType.Date,
  },
  {
    id: 'next_call_in_years',
    label: 'Years to Next Call',
    type: FilterValueType.Number,
  },
  {
    id: 'days_to_maturity_from_next_call',
    label: 'Days to Maturity from Next Call',
    type: FilterValueType.Number,
  },
  {
    id: 'next_call_price',
    label: 'Next Call Price',
    type: FilterValueType.Number,
  },
  {
    id: 'next_coupon_date',
    label: 'Next Coupon Date',
    type: FilterValueType.Date,
  },
  {
    id: 'next_sink_date',
    label: 'Next Sink Date',
    type: FilterValueType.Date,
  },
  {
    id: 'odd_first_coupon',
    label: 'Odd First Coupon',
    type: FilterValueType.String,
  },
  {
    id: 'odd_last_coupon',
    label: 'Odd Last Coupon',
    type: FilterValueType.String,
  },
  {
    id: 'obligor',
    label: 'Obligor',
    type: FilterValueType.String,
  },
  {
    id: 'original_issue_date',
    label: 'Original Issue Date',
    type: FilterValueType.Date,
  },
  {
    id: 'original_issue_price',
    label: 'Original Issue Price',
    type: FilterValueType.Number,
  },
  {
    id: 'original_issue_size',
    label: 'Original Issue Size',
    type: FilterValueType.Number,
  },
  {
    id: 'par_value',
    label: 'Par Value',
    type: FilterValueType.Number,
  },
  {
    id: 'partial_call_announced',
    label: 'Partial Call Announced',
    type: FilterValueType.Boolean,
  },
  {
    id: 'perpetual',
    label: 'Perpetual',
    type: FilterValueType.Boolean,
  },
  {
    // Raw optimizer pricing key used by existing model universe filters;
    // distinct from the transformer-derived eval_price ("Price").
    id: 'price',
    label: 'Live Price',
    type: FilterValueType.Number,
  },
  {
    id: 'principal_factor',
    label: 'Principal Factor',
    type: FilterValueType.Number,
  },
  {
    id: 'private_placement',
    label: 'Private Placement',
    type: FilterValueType.Boolean,
  },
  {
    id: 'prospectus_date',
    label: 'Prospectus Date',
    type: FilterValueType.Date,
  },
  {
    id: 'purpose',
    label: 'Purpose',
    type: FilterValueType.String,
    dropdownOptions: Object.values(Purpose),
  },
  {
    id: 'puttable',
    label: 'Puttable',
    type: FilterValueType.Boolean,
  },
  {
    id: 'qcb_indicator',
    label: 'QCB Indicator',
    type: FilterValueType.Boolean,
  },
  {
    id: 'refund_date',
    label: 'Refund Date',
    type: FilterValueType.Date,
  },
  {
    id: 'refund_price',
    label: 'Refund Price',
    type: FilterValueType.Number,
  },
  {
    id: 'reg_s',
    label: 'Reg S',
    type: FilterValueType.Boolean,
  },
  // {
  //   id: 'run_id',
  //   label: 'Run ID',
  //   type: FilterValueType.String,
  // },
  {
    id: 'sector',
    label: 'Sector',
    type: FilterValueType.String,
    dropdownOptions: Object.values(Sector),
  },
  // Equity classification fields (GICS hierarchy + instrument class).
  {
    id: 'gics_sector_1',
    label: 'GICS Sector',
    type: FilterValueType.String,
    dropdownOptions: Object.values(GicsSector),
  },
  {
    id: 'gics_sector_2',
    label: 'GICS Industry Group',
    type: FilterValueType.String,
    dropdownOptions: Object.values(GicsIndustryGroup),
  },
  {
    id: 'gics_sector_3',
    label: 'GICS Industry',
    type: FilterValueType.String,
    dropdownOptions: Object.values(GicsIndustry),
  },
  {
    id: 'gics_sector_4',
    label: 'GICS Sub-Industry',
    type: FilterValueType.String,
    dropdownOptions: Object.values(GicsSubIndustry),
  },
  {
    id: 'instrument_class',
    label: 'Instrument Class',
    type: FilterValueType.String,
    dropdownOptions: Object.values(InstrumentClass),
  },
  {
    id: 'security_code',
    label: 'Security Code',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SecurityCode),
  },
  {
    id: 'seniority',
    label: 'Seniority',
    type: FilterValueType.String,
    dropdownOptions: Object.values(Seniority),
  },
  {
    id: 'seniority_type',
    label: 'Seniority Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SeniorityType),
  },
  {
    id: 'settlement_convention',
    label: 'Settlement Convention',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SettlementConvention),
  },
  {
    id: 'sink_fund_redemption_method',
    label: 'Sink Fund Redemption Method',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SinkFundRedemptionMethod),
  },
  {
    id: 'sinking_fund',
    label: 'Sinking Fund',
    type: FilterValueType.Boolean,
  },
  {
    id: 'sp_creditwatch',
    label: 'S&P Creditwatch',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SpCreditWatch),
  },
  {
    id: 'sp_creditwatch_date',
    label: 'S&P Creditwatch Date',
    type: FilterValueType.Date,
  },
  {
    id: 'sp_outlook',
    label: 'S&P Outlook',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SpOutlook),
  },
  {
    id: 'sp_outlook_date',
    label: 'S&P Outlook Date',
    type: FilterValueType.Date,
  },
  {
    id: 'sp_rating',
    label: 'S&P Rating',
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(SPRating),
  },
  {
    id: 'sp_rating_date',
    label: 'S&P Rating Date',
    type: FilterValueType.Date,
  },
  {
    id: 'sp_underlying_rating',
    label: 'S&P Underlying Rating',
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(SPRating),
  },
  {
    id: 'sp_underlying_rating_date',
    label: 'S&P Underlying Rating Date',
    type: FilterValueType.Date,
  },
  {
    id: 'moodys_rating',
    label: "Moody's Rating",
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(MoodysRating),
  },
  {
    id: 'moodys_outlook',
    label: "Moody's Outlook",
    type: FilterValueType.String,
    dropdownOptions: Object.values(MoodysOutlook),
  },
  {
    id: 'moodys_outlook_date',
    label: "Moody's Outlook Date",
    type: FilterValueType.Date,
  },
  {
    id: 'moodys_rating_date',
    label: "Moody's Rating Date",
    type: FilterValueType.Date,
  },
  {
    id: 'moodys_underlying_rating',
    label: "Moody's Underlying Rating",
    type: FilterValueType.Rating,
    dropdownOptions: Object.values(MoodysRating),
  },
  {
    id: 'moodys_underlying_rating_date',
    label: "Moody's Underlying Rating Date",
    type: FilterValueType.Date,
  },
  {
    id: 'status',
    label: 'Status',
    type: FilterValueType.String,
    dropdownOptions: Object.values(BondStatus),
  },
  {
    id: 'strip_type',
    label: 'Strip Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(StripType),
  },
  {
    id: 'sukuk_contract_type',
    label: 'Sukuk Contract Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SukukContractType),
  },
  {
    id: 'sukuk_indicator',
    label: 'Sukuk',
    type: FilterValueType.Boolean,
  },
  {
    id: 'sukuk_type',
    label: 'Sukuk Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(SukukType),
  },
  {
    id: 'ticker',
    label: 'Ticker',
    type: FilterValueType.String,
  },
  {
    id: 'ultimate_borrower',
    label: 'Ultimate Borrower',
    type: FilterValueType.String,
  },
  {
    id: 'ultimate_parent',
    label: 'Ultimate Parent',
    type: FilterValueType.String,
  },
  {
    id: 'ultimate_parent_ticker',
    label: 'Ultimate Parent Ticker',
    type: FilterValueType.String,
  },
  {
    id: 'trace_eligible',
    label: 'Trace Eligible',
    type: FilterValueType.Boolean,
  },
  {
    id: 'trading_restrictions_type',
    label: 'Trading Restrictions',
    type: FilterValueType.String,
    dropdownOptions: Object.values(TradingRestrictionsType),
  },
  {
    id: 'treasury_benchmark',
    label: 'Treasury Benchmark',
    type: FilterValueType.String,
  },
  {
    id: 'treasury_subtype',
    label: 'Treasury Subtype',
    type: FilterValueType.String,
    dropdownOptions: Object.values(TreasurySubtype),
  },
  {
    id: 'type',
    label: 'Type',
    type: FilterValueType.String,
    dropdownOptions: Object.values(BondType),
  },
  {
    id: 'updated_at',
    label: 'Updated At',
    type: FilterValueType.Date,
  },
  {
    id: 'us_on_the_run_ind',
    label: 'US On-the-Run',
    type: FilterValueType.Boolean,
  },
  {
    id: 'use_of_proceeds',
    label: 'Use of Proceeds',
    type: FilterValueType.String,
    dropdownOptions: Object.values(UseOfProceeds),
  },
  {
    id: 'vrdo_indicator',
    label: 'VRDO',
    type: FilterValueType.Boolean,
  },
  {
    id: 'workout_date',
    label: 'Workout Date',
    type: FilterValueType.Date,
  },
  {
    id: 'workout_in_years',
    label: 'Years to Workout',
    type: FilterValueType.Number,
  },
  {
    id: 'workout_year',
    label: 'Workout Year',
    type: FilterValueType.Number,
  },
]
